import { ChangeDetectorRef, Component, DOCUMENT, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Subject } from 'rxjs';
import { TranslatePipe } from '@ngx-translate/core';
import { LocalizationService } from '../../Services/localization-service';
import { PagedResponse } from '../../models/page-response';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { ApiService } from '../../Services/api-service';
import { HttpErrorResponse } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { SeoService } from '../../Services/SeoService';

interface Blog {
  id: string;
  title: string;
  summary?: string;
  content?: string;
  createdAt?: string;
  languageId?: string;
  description?: string;
  keywords?: string;
  imageUrl?: string;
  author: string;
  canonicalUrl: string;
  logoUrl: string;
  keywordArray: string[];
}

@Component({
  selector: 'app-blog-component',
  standalone: true,
  imports: [
    CommonModule,
    TranslatePipe,
    RouterModule
  ],
  templateUrl: './blog-component.html',
  styleUrl: './blog-component.scss',
})
export class BlogComponent implements OnInit, OnDestroy {
  private readonly apiService = inject(ApiService);
  private readonly localizationService = inject(LocalizationService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly route = inject(ActivatedRoute);
  private readonly seoService = inject(SeoService);
  private readonly document = inject(DOCUMENT);
  private readonly destroy$ = new Subject<void>();

  blogs: Blog[] = [];
  isLoading = false;
  isLoadingMore = false;
  selectedBlogId: string | null = null;
  pageNumber = 1;
  pageSize = 10;
  hasMoreItems = false;
  currentLang = environment.defaultLang;
  keyword?: string;
  ngOnInit(): void {
    this.localizationService.getLanguage().subscribe(lang => {
      if (lang != '') {
        this.currentLang = lang;
        const canonicalUrl = `${this.document.location.origin}/${lang}/blogs/${this.keyword}`;
        this.seoService.updateCanonicalUrl(canonicalUrl);
        this.cdr.detectChanges();
      }

    });

    this.route.paramMap.subscribe((params) => {
      const keyword = params.get('keyword');
      console.log('Keyword from route:', keyword);
      if (keyword !== null && keyword != undefined) {
        this.keyword = keyword.replace('-', ' ');
      } else {
        this.keyword = '';
      }
      this.loadBlogs();
    });

  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadBlogs(): void {
    this.isLoading = true;
    this.selectedBlogId = null;
    this.pageNumber = 1;

    this.apiService.getData(`api/v1/Blog/GetBlogsByLanguage?pageNumber=${this.pageNumber}&pageSize=${this.pageSize}&keyword=${this.keyword ?? ''}`).subscribe({
      next: (response: any) => {
        const pagedResponse = response?.data as PagedResponse<Blog> | undefined;
        this.blogs = pagedResponse?.items ?? [];
        this.blogs.forEach(blog => {
          blog.keywordArray = blog.keywords?.split(',') ?? [];
        });
        this.hasMoreItems = Boolean(pagedResponse?.hasNextPage);
        this.isLoading = false;
        this.cdr.detectChanges();
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading blogs:', error);
        this.blogs = [];
        this.hasMoreItems = false;
        this.isLoading = false;
        this.cdr.detectChanges();
      },
    });
  }

  loadMoreBlogs(): void {
    if (this.isLoadingMore || !this.hasMoreItems) {
      return;
    }

    this.isLoadingMore = true;
    const nextPage = this.pageNumber + 1;

    this.apiService.getData(`api/v1/Blog/GetBlogsByLanguage?pageNumber=${nextPage}&pageSize=${this.pageSize}&keyword=${this.keyword ?? ''}`).subscribe({
      next: (response: any) => {
        const pagedResponse = response?.data as PagedResponse<Blog> | undefined;
        const nextItems = pagedResponse?.items ?? [];

        if (nextItems.length > 0) {
          this.blogs = [...this.blogs, ...nextItems];
          this.pageNumber = nextPage;
        }

        this.hasMoreItems = Boolean(pagedResponse?.hasNextPage);
        this.isLoadingMore = false;
        this.cdr.detectChanges();
      },
      error: (error: HttpErrorResponse) => {
        console.error('Error loading blogs:', error);
        this.isLoadingMore = false;
        this.cdr.detectChanges();
      },
    });
  }

  toggleBlog(blog: Blog): void {
    this.selectedBlogId = this.selectedBlogId === blog.id ? null : blog.id;
  }

  isExpanded(blog: Blog): boolean {
    return this.selectedBlogId === blog.id;
  }
}
